import React, { Suspense, useState } from 'react';
import { SubmitHandler, Controller, useForm } from 'react-hook-form';
import {
  IUserForgotPasswordReq,
  IUserForgotPasswordRes,
} from '@models/interfaces/user.interface';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { AxiosResponse } from 'axios';
import { useNavigate } from 'react-router-dom';
import userService from '@services/GeniebookAPI/UserServices.services';
import { Input, Button } from 'geniebook-ui/dist';

export interface propsType {
  backToLoginCallback?: () => void;
}
//validation login
const spacesOnly = (string: string) => /^(\s+$)/g.test(string);
const schema = yup
  .object({
    username: yup
      .string()
      .required('Enter username or email.')
      .test(
        'Enter username or email.',
        'Enter username or email.',
        value => !spacesOnly(value?.toString() ?? ''),
      ),
  })
  .required();

export default function ForgotPasswordCard({ backToLoginCallback }: propsType) {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState({
    error: false,
    error_msg: '',
    error_code: '',
  });
  const [emailSent, setEmailSent] = useState<string>('');

  const initialValues: IUserForgotPasswordReq = {
    username: '',
    user_type: 1,
  };

  const {
    handleSubmit,
    control,
    formState: { errors },
  } = useForm<IUserForgotPasswordReq>({
    defaultValues: initialValues,
    resolver: yupResolver(schema),
  });

  const onSubmit: SubmitHandler<IUserForgotPasswordReq> = async data => {
    setLoading(true);
    setError({
      error: false,
      error_msg: '',
      error_code: '',
    });
    try {
      const res: AxiosResponse = await userService.forgotPassword(data);
      const body: IUserForgotPasswordRes = res.data;
      if (body.error) {
        setError({
          ...error,
          error: body.error,
          error_msg: body.error_msg,
          error_code: body.error_code,
        });
      } else {
        setEmailSent(data.username);
      }
      setLoading(false);
    } catch (error) {
      setLoading(false);
      setError({
        error: true,
        error_msg: 'Unknown Error',
        error_code: '',
      });
    }
  };

  return emailSent ? (
    <>
      <div className="font-black text-primary-navyblue text-[32px] leading-[34px] mb-[20px]">
        Check Your Email
      </div>
      <div className="text-[16px] leading-[24px] text-neutral-pencil mb-[16px]">
        A link to reset your password has been sent to{' '}
        <strong>{emailSent}</strong>.
      </div>
      <div className="text-[16px] leading-[24px] text-neutral-pencil mb-[32px]">
        Can't find the email? Try checking your spam folder.
      </div>
      <Button
        className="!max-w-none w-full mb-[19px]"
        variant="primary"
        onClick={
          backToLoginCallback !== undefined
            ? backToLoginCallback
            : () => navigate('/')
        }
      >
        Return to Login
      </Button>
    </>
  ) : (
    <>
      <div className="font-black text-primary-navyblue text-[32px] leading-[34px] mb-[20px]">
        Forgot Password?
      </div>
      <div className="text-[16px] leading-[24px] text-neutral-pencil mb-[32px]">
        Enter your username or email and we will send you a link to reset your
        password.
      </div>
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="mb-4">
          <Controller
            name="username"
            control={control}
            render={({ field }) => (
              <Input
                {...field}
                mode={'light'}
                placeholder="Type Username / Email"
                className="w-full"
                label="Username / Email"
                error={
                  error.error
                    ? 'An email to reset your password has been sent recently. Please try again later.'
                    : errors.username
                    ? errors.username.message
                    : ''
                }
              />
            )}
          />
        </div>
        <Suspense>
          <button className="w-full">
            <Button className="!max-w-none w-full mt-4" variant="primary">
              {loading ? 'Sending...' : 'Submit'}
            </Button>
          </button>
        </Suspense>
      </form>
    </>
  );
}
